package or.app.orientalrecepies.network.repository

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.launch
import or.app.orientalrecepies.network.HttpClientProvider
import or.app.orientalrecepies.network.models.ApiResponse
import or.app.orientalrecepies.network.models.Category
import or.app.orientalrecepies.network.models.FavoriteRequest
import or.app.orientalrecepies.network.models.Recipe
import or.app.orientalrecepies.network.models.ResultState


internal class RecipeApi {
    suspend fun getRecipes(
        searchText: String? = null,
        category: String? = null,
        activeOnly: Boolean = true
    ): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.get("recipes") {
            url {
                searchText?.let { parameters.append("searchText", it) }
                category?.let { parameters.append("category", it) }
                parameters.append("activeOnly", activeOnly.toString())
            }
        }
    }

    suspend fun getRecipeById(id: String): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.get("recipes/$id")
    }

    suspend fun getPromotedRecipes(): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.get("recipes/promoted")
    }

    suspend fun getCategories(): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.get("categories")
    }

    suspend fun addCategory(category: Category): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.post("categories") {
            setBody(category)
        }
    }

    suspend fun updateCategory(category: Category): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.put("categories/${category.id}") {
            setBody(category)
        }
    }

    suspend fun deleteCategory(id: String): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.delete("categories/$id")
    }

    suspend fun getFavourites(id: String): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.get("favorites/$id")
    }

    suspend fun addToFavorites(request: FavoriteRequest): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.post("favorites") {
            setBody(request)
        }
    }

    suspend fun removeFromFavorites(request: FavoriteRequest): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.delete("favorites") {
            setBody(request)
        }
    }

    suspend fun deleteRecipe(id: String): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.delete("recipes/$id")
    }

    suspend fun addRecipe(recipe: Recipe): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.post("recipes") {
            setBody(recipe)
        }
    }

    suspend fun generateAiRecipe(ingredients: List<String>): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.post("ai/recipe") {
            setBody(ingredients)
        }
    }

    suspend fun updateRecipe(recipe: Recipe): Result<HttpResponse> = runCatching {
        return@runCatching HttpClientProvider.client.put("recipes/${recipe.id}") {
            setBody(recipe)
        }
    }

    suspend fun uploadImage(bytes: ByteArray, fileName: String): Result<HttpResponse> =
        runCatching {
            return@runCatching HttpClientProvider.client.post("upload") {
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append("image", bytes, Headers.build {
                                append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                                append(HttpHeaders.ContentType, "image/jpeg")
                            })
                        }
                    )
                )
            }
        }

    suspend fun togglePromoted(recipeId: String, promoted: Boolean): Result<HttpResponse> =
        runCatching {
            return@runCatching HttpClientProvider.client.post("recipes/$recipeId/promoted") {
                url { parameters.append("promoted", promoted.toString()) }
            }
        }
}

internal object RecipeRepository {

    fun getRecipes(
        searchText: String? = null,
        category: String? = null,
        activeOnly: Boolean = true
    ): Flow<ResultState<List<Recipe>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .getRecipes(searchText, category, activeOnly)
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body<List<Recipe>>()))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load recipe. Please try again!"))
        }
    }.flowOn(Dispatchers.Default)

    fun getRecipeById(id: String): Flow<ResultState<Recipe>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .getRecipeById(id)
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body<Recipe>()))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load recipe. Please try again!"))
        }
    }.flowOn(Dispatchers.Default)

    fun getPromotedRecipes(): Flow<ResultState<List<Recipe>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .getPromotedRecipes()
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body<List<Recipe>>()))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load promoted recipes."))
        }
    }.flowOn(Dispatchers.Default)

    fun getCategories(): Flow<ResultState<List<Category>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .getCategories()
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body<List<Category>>()))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load categories. Please try again!"))
        }
    }.flowOn(Dispatchers.Default)

    fun addCategory(category: Category): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .addCategory(category)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to add category"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun updateCategory(category: Category): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .updateCategory(category)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to update category"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun deleteCategory(id: String): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .deleteCategory(id)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to delete category"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun uploadImage(bytes: ByteArray, fileName: String): Flow<ResultState<String>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi().uploadImage(bytes, fileName).getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                val body = response.body<Map<String, String>>()
                emit(ResultState.Success(body["url"] ?: ""))
            } else {
                emit(ResultState.Error("Upload failed"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message ?: "Unknown error"))
        }
    }.flowOn(Dispatchers.Default)

    fun getFavourites(id: String): Flow<ResultState<List<Recipe>>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .getFavourites(id)
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body<List<Recipe>>()))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load recipe. Please try again!"))
        }
    }.flowOn(Dispatchers.Default)

    fun toggleFavorite(
        deviceId: String,
        recipeId: String,
        isAdd: Boolean
    ): Flow<ResultState<String>> = flow {
        emit(ResultState.Loading)
        try {
            val request = FavoriteRequest(deviceId, recipeId)
            val response = if (isAdd) {
                RecipeApi().addToFavorites(request)
            } else {
                RecipeApi().removeFromFavorites(request)
            }.getOrNoInternet()

            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success("Uspješno!"))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Došlo je do greške prilikom promjene favorita."))
        }
    }.flowOn(Dispatchers.Default)

    fun removeFromFavorites(request: FavoriteRequest): Flow<ResultState<String>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .removeFromFavorites(request)
                .getOrNoInternet()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success("Uspješno!"))
            } else {
                emit(ResultState.Error(response.body<ApiResponse<String>>().message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Došlo je do greške prilikom uklanjanja iz favorita."))
        }
    }.flowOn(Dispatchers.Default)

    fun deleteRecipe(id: String): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .deleteRecipe(id)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to delete recipe"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun addRecipe(recipe: Recipe): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .addRecipe(recipe)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to add recipe"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun generateAiRecipe(ingredients: List<String>): Flow<ResultState<Recipe>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi().generateAiRecipe(ingredients).getOrThrow()
            delay(1000)
            if (response.status.isSuccess()) {
                val body = response.body<Recipe>()
                emit(ResultState.Success(body))
            } else {
                emit(ResultState.Error("Trenutno nije moguće generisati recept. Pokušajte ponovo!"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun updateRecipe(recipe: Recipe): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .updateRecipe(recipe)
                .getOrThrow()
            delay(250)
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to update recipe"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)

    fun togglePromoted(recipeId: String, promoted: Boolean): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        try {
            val response = RecipeApi()
                .togglePromoted(recipeId, promoted)
                .getOrThrow()
            if (response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error("Failed to update promotion status"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error(e.message))
        }
    }.flowOn(Dispatchers.Default)
}

data object RecipeService {
    fun getRecipes(
        searchText: String? = null,
        category: String? = null,
        activeOnly: Boolean = true,
        result: (ResultState<List<Recipe>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .getRecipes(searchText, category, activeOnly)
                .collect {
                    result(it)
                }
        }
    }

    fun getRecipeById(
        id: String,
        result: (ResultState<Recipe>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .getRecipeById(id)
                .collect {
                    result(it)
                }
        }
    }

    fun getPromotedRecipes(
        result: (ResultState<List<Recipe>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .getPromotedRecipes()
                .collect {
                    result(it)
                }
        }
    }

    fun getCategories(
        result: (ResultState<List<Category>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .getCategories()
                .collect {
                    result(it)
                }
        }
    }

    fun addCategory(
        category: Category,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .addCategory(category)
                .collect {
                    result(it)
                }
        }
    }

    fun updateCategory(
        category: Category,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .updateCategory(category)
                .collect {
                    result(it)
                }
        }
    }

    fun deleteCategory(
        id: String,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .deleteCategory(id)
                .collect {
                    result(it)
                }
        }
    }

    fun uploadImage(bytes: ByteArray, fileName: String, result: (ResultState<String>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository.uploadImage(bytes, fileName).collect { result(it) }
        }
    }

    fun getFavourites(
        id: String,
        result: (ResultState<List<Recipe>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .getFavourites(id)
                .collect {
                    result(it)
                }
        }
    }

    fun toggleFavorite(
        deviceId: String,
        recipeId: String,
        isAdd: Boolean,
        result: (ResultState<String>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .toggleFavorite(deviceId, recipeId, isAdd)
                .collect {
                    result(it)
                }
        }
    }

    fun removeFromFavorites(
        request: FavoriteRequest,
        result: (ResultState<String>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .removeFromFavorites(request)
                .collect {
                    result(it)
                }
        }
    }

    fun deleteRecipe(
        id: String,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .deleteRecipe(id)
                .collect {
                    result(it)
                }
        }
    }

    fun addRecipe(
        recipe: Recipe,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .addRecipe(recipe)
                .collect {
                    result(it)
                }
        }
    }

    fun generateAiRecipe(
        ingredients: List<String>,
        result: (ResultState<Recipe>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .generateAiRecipe(ingredients)
                .collect {
                    result(it)
                }
        }
    }

    fun updateRecipe(
        recipe: Recipe,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .updateRecipe(recipe)
                .collect {
                    result(it)
                }
        }
    }

    fun togglePromoted(
        recipeId: String,
        promoted: Boolean,
        result: (ResultState<Unit>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            RecipeRepository
                .togglePromoted(recipeId, promoted)
                .collect {
                    result(it)
                }
        }
    }
}

fun Result<HttpResponse>.getOrNoInternet(): HttpResponse {
    return this.getOrElse {
        if (it.message?.contains("Unable to resolve host") == true) {
            throw Exception("Nema internet konekcije, provjerite Vašu vezu i pokušajte ponovo.")
        }
        throw Exception("Došlo je do greške. Pokušajte ponovo!")
    }
}